'use client'

import { memo, useCallback } from 'react'
import { CompactTag } from '@/ui/compact-tag'
import {
  CakeIcon,
  CaretRightIcon,
  ClockIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react'
import { EditPersonDialog } from './edit-person-dialog'
import { ContactFrequencySelector } from './contact-frequency-selector'
import { Button } from '@/ui/button'
import {
  usePersonEntries,
  type PersonWithLastInteraction,
} from './select-people'
import { useDocumentOps } from '@/document'
import {
  PERSON_CATEGORY_COLORS,
  PERSON_CATEGORY_LABELS,
  PERSON_AVATAR_COLORS,
} from './category-colors'
import { formatRelativeTime, formatDate, formatBirthday } from '@/lib/date'
import { cn } from '@/lib/tailwind'
import type { ContactFrequency } from './meta'
import { EntriesTimeline } from './EntriesTimeline'
import { EntriesTimelineSkeleton } from './EntriesTimelineSkeleton'
import { EntriesErrorState } from './EntriesErrorState'

export const PersonDetailPanel = memo(function PersonDetailPanel({
  person,
  onClose,
}: {
  person: PersonWithLastInteraction
  onClose: () => void
}) {
  const {
    data: entries,
    isLoading,
    error,
    refetch,
  } = usePersonEntries(person.id)
  const ops = useDocumentOps()

  const handleFrequencyChange = useCallback(
    (value: ContactFrequency | null) => {
      // `undefined` in a metadata patch clears the key, which is what the null option means here
      ops.updateEntry(person.id, {
        metadata: { contactFrequency: value ?? undefined },
      })
    },
    [ops, person.id],
  )

  return (
    <div className="flex-1">
      {/* Mobile back button */}
      <button
        type="button"
        onClick={onClose}
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring mb-4 flex items-center gap-1 text-sm focus-visible:ring-2 focus-visible:outline-none lg:hidden"
      >
        <CaretRightIcon className="h-3.5 w-3.5 rotate-180" aria-hidden="true" />
        Back to list
      </button>

      {/* Person header */}
      <div className="mb-6">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-semibold',
              PERSON_AVATAR_COLORS[
                person.metadata.category as 'family' | 'friend' | 'work'
              ],
              'text-foreground',
            )}
            aria-hidden="true"
          >
            {person.title.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h2 className="truncate text-lg font-semibold">{person.title}</h2>
              <EditPersonDialog
                person={person}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground -mt-1 -mr-2 h-8 w-8 shrink-0 p-0"
                  >
                    <PencilSimpleIcon className="h-4 w-4" />
                    <span className="sr-only">Edit {person.title}</span>
                  </Button>
                }
              />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <CompactTag
                variant="colored"
                size="sm"
                className={
                  PERSON_CATEGORY_COLORS[
                    person.metadata.category as 'family' | 'friend' | 'work'
                  ]
                }
              >
                {
                  PERSON_CATEGORY_LABELS[
                    person.metadata.category as 'family' | 'friend' | 'work'
                  ]
                }
              </CompactTag>
              {typeof person.metadata.birthday === 'string' && (
                <span className="text-muted-foreground flex items-center gap-1 text-xs">
                  <CakeIcon className="h-3 w-3" aria-hidden="true" />
                  {formatBirthday(person.metadata.birthday)}
                </span>
              )}
            </div>
            {person.content && (
              <p className="text-muted-foreground mt-2 line-clamp-3 text-sm">
                {person.content}
              </p>
            )}
          </div>
        </div>

        {/* Last interaction */}
        {person.lastInteraction ? (
          <div
            className="text-muted-foreground mt-3 flex items-center gap-1.5 text-sm"
            title={formatDate(person.lastInteraction)}
          >
            <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              Last interaction: {formatRelativeTime(person.lastInteraction)}
            </span>
          </div>
        ) : (
          <div className="text-muted-foreground/60 mt-3 flex items-center gap-1.5 text-sm">
            <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>No interactions yet</span>
          </div>
        )}

        {/* Contact frequency */}
        <div className="mt-3 flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Stay in touch:</span>
          <ContactFrequencySelector
            value={
              (person.metadata.contactFrequency as string | undefined) ?? null
            }
            onChange={handleFrequencyChange}
          />
        </div>
      </div>

      {/* Entries timeline with error handling */}
      {isLoading ? (
        <EntriesTimelineSkeleton />
      ) : error ? (
        <EntriesErrorState onRetry={refetch} />
      ) : entries ? (
        <EntriesTimeline entries={entries} />
      ) : null}
    </div>
  )
})
